// services/NotificationService.ts
import { apiClient } from './authService';

const APP_ID = process.env.REACT_APP_NOTIFICATION_APP_ID || 'booking-core-app';
const TEMPLATE_CODE =
  process.env.REACT_APP_NOTIFICATION_TEMPLATE || 'GENERIC_MESSAGE'; // set to an existing template code on notification service

const buildNotification = (title: string, message: string, extraData?: any) => ({
  appId: APP_ID,
  templateCode: TEMPLATE_CODE,
  language: 'en',
  channels: ['EMAIL', 'SMS', 'PUSH', 'WEB_PUSH', 'IN_APP'],
  data: { title, message, ...extraData },
});

const normalizeId = (value: any): string | undefined => {
  if (value === undefined || value === null) return undefined;
  const normalized = String(value).trim();
  return normalized ? normalized : undefined;
};

const normalizeIds = (value: any): string[] => {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return list
    .map((item) => normalizeId(item))
    .filter((item): item is string => Boolean(item));
};

const resolveMessage = (payload: any): string =>
  String(payload?.message ?? payload?.body ?? "").trim();

const postBroadcast = async (
  target: Record<string, any>,
  title: string,
  message: string
): Promise<any> => {
  const res = await apiClient.post("/broadcast", {
    target,
    notification: buildNotification(title, message),
  });
  return res.data;
};

// v1 name preserved
export const getAllNotifications = async (params?: {
  startDate?: string;
  endDate?: string;
}): Promise<any> => {
  const res = await apiClient.get(`/notification/notifications/all`, {
    params: {
      ...(params?.startDate ? { startDate: params.startDate } : {}),
      ...(params?.endDate ? { endDate: params.endDate } : {}),
    },
  });
  return res.data;
};

// v1 name preserved
export const sendAllNotification = async (payload: any): Promise<any> => {
  const title = String(payload?.title ?? "").trim();
  const message = resolveMessage(payload);
  const unitId = normalizeId(
    payload?.unitId ?? payload?.branchId ?? payload?.business_id
  );

  if (!unitId) {
    throw new Error("unitId is required for UNIT_ALL target.");
  }

  return postBroadcast(
    {
      type: "UNIT_ALL",
      unitId,
    },
    title,
    message
  );
};

// v1 name preserved
export const sendNotificationToClient = async (payload: any): Promise<any> => {
  const title = String(payload?.title ?? "").trim();
  const message = resolveMessage(payload);
  const unitId = normalizeId(
    payload?.unitId ?? payload?.branchId ?? payload?.business_id
  );
  const communityIds = normalizeIds(payload?.communityIds ?? payload?.group_id);
  const userIds = normalizeIds(payload?.userIds ?? payload?.client_ids);

  if (communityIds.length > 0) {
    return postBroadcast(
      {
        type: "COMMUNITIES",
        communityIds,
        ...(unitId ? { unitId } : {}),
      },
      title,
      message
    );
  }

  if (userIds.length === 0) {
    throw new Error("userIds is required for CUSTOM_USERS target.");
  }

  return postBroadcast(
    {
      type: "CUSTOM_USERS",
      userIds,
      ...(unitId ? { unitId } : {}),
    },
    title,
    message
  );
};

// v1 name preserved
export const sendNotificationToVenue = async (payload: any): Promise<any> => {
  const title = String(payload?.title ?? "").trim();
  const message = resolveMessage(payload);
  const businessId = normalizeId(payload?.businessId ?? payload?.venueId ?? payload?.venue_id);
  const unitId = normalizeId(payload?.unitId ?? payload?.branchId);

  if (businessId) {
    return postBroadcast(
      {
        type: "BUSINESS_ALL",
        businessId,
      },
      title,
      message
    );
  }

  if (unitId) {
    return postBroadcast(
      {
        type: "UNIT_ALL",
        unitId,
      },
      title,
      message
    );
  }

  throw new Error("businessId is required for BUSINESS_ALL target.");
};

// v1 name preserved
export const sendNotificationToOrganization = async (payload: any): Promise<any> => {
  const title = String(payload?.title ?? "").trim();
  const message = resolveMessage(payload);
  return postBroadcast(
    {
      type: "ORGANIZATION_ALL",
    },
    title,
    message
  );
};
